import React, { useCallback, useMemo } from 'react';
import { Box, Button, Hint, Text } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Access } from '@/modules/core/components';
import { useCurrentOrganization } from '@/modules/core/hooks';
// import { Access } from '@/modules/core/components';
import { EmployerStatus } from '@/modules/employer/employer.constants';

import { TEMPLATE_UNDER_UPDATE_HINT } from '../PlanSetup.constants';
import { useEditModeStore } from '../stores';

interface Props {
  isDraftMode?: boolean;
  isPlanActive?: boolean;
  isTemplateMode?: boolean;
  isPlanTerminated?: boolean;
  isTemplateUnderUpdate?: boolean;
}

const PlanTopbarEditModeButton: React.FC<Props> = ({
  isDraftMode,
  isTemplateMode,
  isPlanActive,
  isPlanTerminated,
  isTemplateUnderUpdate,
}) => {
  const editPermission = useMemo(() => {
    if (isTemplateMode) {
      return isPlanActive ? Permissions.MODIFY_ACTIVE_PLAN_TEMPLATE : Permissions.PLAN_TEMPLATE_SETUP;
    }
    return Permissions.EDIT_DRAFT_PLAN;
  }, [isTemplateMode, isPlanActive]);
  const setEditMode = useEditModeStore((state) => state.setEditMode);
  const {
    observingOrganization: { type, status },
  } = useCurrentOrganization();

  const isTerminationStatus = useMemo(
    () =>
      status === EmployerStatus.PendingTermination ||
      status === EmployerStatus.TerminationInProgress ||
      status === EmployerStatus.Terminated,
    [status]
  );

  const handleSetEditMode = useCallback(() => {
    setEditMode(true);
  }, [setEditMode]);

  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  return (
    <Box direction="row">
      <Box width="100px">
        {!isSubsidiary && (
          <Access
            accessRequirements={[
              {
                permission: editPermission,
              },
            ]}
          >
            <Hint
              hideHint={!isTerminationStatus && !isPlanTerminated && !isTemplateUnderUpdate}
              hintAlign="vertical"
              hintWidth={220}
              hintElement={
                <Button
                  secondary
                  style={{ flex: 1, padding: 0 }}
                  onClick={handleSetEditMode}
                  type="button"
                  size="medium"
                  disabled={isTerminationStatus || isPlanTerminated || isTemplateUnderUpdate}
                  label={isDraftMode || isPlanActive || isTemplateUnderUpdate ? 'Edit' : 'Configure'}
                />
              }
            >
              <Text size="small" color="textOnColor" textAlign="center">
                {isTemplateUnderUpdate
                  ? TEMPLATE_UNDER_UPDATE_HINT
                  : isPlanTerminated
                    ? 'Terminated plans cannot be edited.'
                    : 'Terminated organizations cannot edit plan.'}
              </Text>
            </Hint>
          </Access>
        )}
      </Box>
    </Box>
  );
};

export default PlanTopbarEditModeButton;
